import {
  MenuDivider,
  RichTextField,
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonUnderline,
  MenuButtonBulletedList,
  MenuButtonTextColor,
  MenuControlsContainer,
  RichTextEditorProvider,
} from 'mui-tiptap';
import DOMPurify from 'dompurify';
import { useIntl } from 'react-intl';
import type { ReactNode } from 'react';
import { handleRichTextTabKeyDown, normalizeToHtml, TAB_SIZE } from 'utils';
import { useEditor } from '@tiptap/react';
import Link from '@tiptap/extension-link';
import { useEffect, useMemo } from 'react';
import Color from '@tiptap/extension-color';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Box, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import RichTextReadOnlyWithLinkCopy from 'components/rich-text-read-only-with-link-copy';

type Props = {
  id: string;
  value: string;
  editable: boolean;
  onChange: (id: string, html: string) => void;

  label?: ReactNode;
  size?: 'small' | 'medium';
  fullWidth?: boolean;

  minHeight?: number;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;

  sx?: any;
};

const ResizableRichTextOutlinedField = ({
  id,
  value,
  editable,
  onChange,
  label,
  size = 'small',
  fullWidth = true,
  minHeight = 110,
  placeholder,
  disabled,
  ariaLabel,
  sx,
}: Props) => {
  const intl = useIntl();

  const extensions = useMemo(
    () => [
      StarterKit,
      TextStyle,
      Color,
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          style: 'color: inherit;',
        },
      }),
    ],
    []
  );

  const editor = useEditor({
    extensions,
    content: normalizeToHtml(value || ''),
    editable: editable && !disabled,
    enableInputRules: false,
    editorProps: {
      handleKeyDown: handleRichTextTabKeyDown,
      attributes: {
        'data-placeholder': placeholder ?? '',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
    },
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const safe = DOMPurify.sanitize(html);
      onChange(id, safe);
    },
  });

  const isEmpty = !value || !value.replace(/<[^>]*>/g, '').trim();

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable && !disabled);
  }, [editor, editable, disabled]);

  useEffect(() => {
    if (!editor) return;
    if (editor.isFocused) return;

    const next = normalizeToHtml(value || '');
    const current = editor.getHTML();

    if (current !== next) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, value]);

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      variant="outlined"
      sx={{
        ...sx,
        '& .MuiOutlinedInput-root': {
          alignItems: 'flex-start',
          height: 'auto',
          minHeight: isEmpty ? 40 : 'auto',
        },
      }}>
      {label && (
        <InputLabel shrink htmlFor={id}>
          {label}
        </InputLabel>
      )}

      <OutlinedInput
        id={id}
        notched={!!label}
        label={typeof label === 'string' ? label : undefined}
        readOnly
        value=""
        inputProps={{ style: { display: 'none' }, 'aria-label': ariaLabel }}
        startAdornment={
          <Box
            sx={{ width: '100%' }}
            role={!editable ? 'group' : undefined}
            aria-label={!editable ? ariaLabel : undefined}
            onClick={(e) => e.stopPropagation()}>
            {editable ? (
              editor ? (
                <RichTextEditorProvider editor={editor}>
                  <RichTextField
                    variant="outlined"
                    controls={
                      <MenuControlsContainer>
                        <MenuButtonBold tooltipLabel={intl.locale === 'tr' ? 'Kalın' : 'Bold'} />
                        <MenuButtonItalic tooltipLabel={intl.locale === 'tr' ? 'Eğik' : 'Italic'} />
                        <MenuButtonUnderline tooltipLabel={intl.locale === 'tr' ? 'Altı çizili' : 'Underline'} />
                        <MenuDivider />
                        <MenuButtonBulletedList
                          tooltipLabel={intl.formatMessage({ id: 'richText.bulletedList' })}
                        />
                        <MenuButtonOrderedList
                          tooltipLabel={intl.formatMessage({ id: 'richText.numberedList' })}
                        />
                        <MenuDivider />
                        <MenuButtonTextColor
                          popperId={`text-color-${id}`}
                          swatchColors={['#f00', '#0f0', '#00f', '#ff0', '#f0f', '#0ff', '#000', '#fff']}
                          tooltipLabel={intl.locale === 'tr' ? 'Yazı Rengi' : 'Text Color'}
                          labels={{
                            removeColorButton: intl.locale === 'tr' ? 'Sıfırla' : 'Reset',
                            cancelButton: intl.locale === 'tr' ? 'Vazgeç' : 'Cancel',
                            saveButton: intl.locale === 'tr' ? 'KAYDET' : 'OK',
                            textFieldPlaceholder: `${intl.locale === 'tr' ? 'Ör' : 'Ex'}: "#7cb5ec"`,
                          }}
                        />
                      </MenuControlsContainer>
                    }
                    RichTextContentProps={{
                      sx: {
                        minHeight,
                        '& .ProseMirror:before': isEmpty
                          ? {
                              content: `"${placeholder ?? ''}"`,
                              color: 'text.disabled',
                              pointerEvents: 'none',
                            }
                          : {},

                        '& .ProseMirror': {
                          outline: 'none',
                          whiteSpace: 'break-spaces',
                          tabSize: TAB_SIZE,
                        },

                        '& .ProseMirror a': {
                          color: 'inherit',
                          textDecorationColor: 'inherit',
                        },
                        '& .ProseMirror a:hover': {
                          color: 'inherit',
                        },
                      },
                    }}
                  />
                </RichTextEditorProvider>
              ) : null
            ) : (
              <Box
                sx={{
                  px: '14px',
                  py: isEmpty ? 0.5 : 1,
                  whiteSpace: 'break-spaces',
                  tabSize: TAB_SIZE,
                  wordBreak: 'break-word',

                  '& a': { color: 'inherit', textDecorationColor: 'inherit' },

                  minHeight: isEmpty ? 24 : 'auto',
                  overflow: 'auto',
                }}>
                <RichTextReadOnlyWithLinkCopy value={value || ''} />
              </Box>
            )}
          </Box>
        }
      />
    </FormControl>
  );
};

export default ResizableRichTextOutlinedField;
